import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import {
  NOTICE_COLUMN_MAX_PX,
  NOTICE_COMPACT_MAX_WIDTH,
  NOTICE_FADE_MS,
  noticeStyles,
} from '../../../theme/notice.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { noticeFlagStyle, noticeName, renderNoticeBadge, renderNoticeTally } from '../notice-player';

/**
 * Death Notice - "Destroyed by..." notification overlay (top 50%)
 *
 * Displays a notification when the player is killed by another player.
 * Shows the killer's chip (flag, name, level or bot badge), then the score
 * change and any upgrades that arrived in the same score update.
 * Auto-dismisses after timeout with fade-out animation.
 *
 * @element death-notice
 */
@customElement('death-notice')
export class DeathNotice extends LitElement {
  static override styles = [
    tokens,
    noticeStyles,
    css`
      /* The host, the pop/fade animations, the player chip (.playerbig,
         legacy's roomier chip for this bigger heading), flag, badges and the
         score and upgrade lines come from noticeStyles, shared with
         kill-notice; only the notice's placement and size are this one's. */
      .notice {
        top: 50%;
        width: 80%;
        left: 10%;
        font-size: 50px;
      }

      /* Tablet (same query as noticeStyles' chip rule) */
      @media (min-width: 600px) and (max-width: ${unsafeCSS(NOTICE_COMPACT_MAX_WIDTH)}px) {
        .notice {
          font-size: 30px;
        }
      }

      /* Phone, portrait or landscape — after tablet, which a landscape phone
         also matches. A block of airmash-game's notice column there, so the
         placement above is zeroed (noticeStyles has the rest). */
      @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
        .notice {
          font-size: var(--font-size-xl);
          top: auto;
          left: auto;
          /* Wider than the desktop's 80%, so the chip wraps less and the
             column stays short */
          width: 90%;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismissTimer: number | null = null;
  private fadeTimer: number | null = null;
  private lastNoticeId: number = -1;
  private hiding = false;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearTimers();
  }

  private clearTimers() {
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.fadeTimer !== null) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  override updated() {
    const notice = this.uiState.deathNotice;

    if (!notice) {
      this.hiding = false;
      this.lastNoticeId = -1;
      this.clearTimers();
      return;
    }

    // Already timing this one. A death notice is built whole from one score
    // update and never changes after, so this is an unrelated re-render.
    if (notice.id === this.lastNoticeId) return;

    // New notice — cancel any in-progress fade and reset.
    const wasHiding = this.hiding;
    this.hiding = false;
    this.lastNoticeId = notice.id;
    this.clearTimers();
    // hiding flips AFTER this cycle's render, so a death landing during the
    // previous notice's 1.5s fade would have rendered the new (re-keyed) notice
    // already carrying the .hiding class — replaying msghide and vanishing
    // instantly. Re-render so it shows steadily; the follow-up updated()
    // early-returns (id now matches), so there's no loop.
    if (wasHiding) this.requestUpdate();

    // Phase 1: after timeout, start fade-out
    const capturedId = notice.id;
    this.dismissTimer = window.setTimeout(() => {
      this.dismissTimer = null;
      const current = this.uiState.deathNotice;
      if (!current || current.id !== capturedId) return;

      this.hiding = true;
      this.requestUpdate();

      // Phase 2: after fade animation, clear the state
      this.fadeTimer = window.setTimeout(() => {
        this.fadeTimer = null;
        const stillCurrent = this.uiState.deathNotice;
        if (stillCurrent && stillCurrent.id === capturedId) {
          this.hiding = false;
          this.lastNoticeId = -1;
          this.gameApi.dismissDeathNoticeIfMatching(capturedId);
        }
      }, NOTICE_FADE_MS);
    }, notice.timeoutMs);
  }

  override render() {
    const notice = this.uiState.deathNotice;
    if (!notice) return nothing;

    const killer = notice.killer;
    // Legacy never capped the killer's name; the compact breakpoints do, where
    // a long one overflows the notice.
    const capName = this.uiState.viewport.width <= NOTICE_COMPACT_MAX_WIDTH;

    return keyed(
      notice.id,
      html`
        <div class="notice${this.hiding ? ' hiding' : ''}">
          Destroyed by
          <span class="playerbig">
            <span class="flag" style=${noticeFlagStyle(killer.flag)}></span>
            ${noticeName(killer.name, capName)}${renderNoticeBadge(killer)}
          </span>
          ${renderNoticeTally(notice.scoreChange, notice.upgradesChange)}
        </div>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'death-notice': DeathNotice;
  }
}
